import {WidgetConfig} from "types/config";
import {ChatWidget} from "classes/chat/chatWidget";
import {Alpine} from "@alpinejs/csp";
import ComponentConfig from "config/componentConfig";
import {
    CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT,
    CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT,
    CHAT_MESSAGE_INPUT_REQUEST_HAS_TEXT_EVENT,
    IChatMessageInputHasTextEventDetails,
    IChatMessageInputFillRecordedTextEventDetails
} from "types/events";
import {logContext} from "classes/utils/graylogLogger";

import AlpineComponent = Alpine.AlpineComponent;
import AlpineMagics = Alpine.Magics;

type SpeechMessageFactory = (
    widget: ChatWidget,
    widgetConfig: WidgetConfig,
    componentConfig: ComponentConfig
) => AlpineComponent<SpeechMessage>;

type PausedChunk = {
    data: BlobPart;
    recordedAt: number;
};

type SpeechToTextResponseData = {
    message: string;
    errorCodeGroup: string;
};

type SpeechToTextUrlProvider = {
    getSpeechToTextUrl: () => string;
};

type AndroidBridge = {
    checkMicrophonePermissions: () => Promise<unknown> | unknown;
    requestMicrophonePermissions: () => void;
};

type WebkitMessageHandler = {
    postMessage: (payload: Record<string, never>) => Promise<unknown> | unknown;
};

type WebkitMessageHandlers = {
    checkMicrophonePermissions?: WebkitMessageHandler;
    requestMicrophonePermissions?: WebkitMessageHandler;
};

type NavigatorWithPermissions = Navigator & {
    permissions?: {
        query: (permissionDescriptor: PermissionDescriptor) => Promise<PermissionStatus>;
    };
};

type BrowserWindow = Window & {
    androidBridge?: AndroidBridge;
    webkit?: { messageHandlers?: WebkitMessageHandlers };
    microphonePermissionRequestCompleted?: (status: string) => Promise<void>;
};

class SpeechMessage {
    private static readonly CHAT_MESSAGE_INPUT_SELECTOR: string = '#chat-widget-message-input';
    startBtn: HTMLButtonElement | null = null;
    cancelBtn: HTMLButtonElement | null = null;
    stopBtn: HTMLButtonElement | null = null;
    sendBtn: HTMLButtonElement | null = null;
    statusDiv: HTMLElement | null = null;
    audioPlayback: HTMLAudioElement | null = null;
    recordingCanvas: HTMLCanvasElement | null = null;
    recordingTimer: HTMLSpanElement | null = null;
    recordingStartedAt: number | null = null;

    private readonly speechMessageComponent: AlpineMagics<SpeechMessage>;
    private mediaRecorder: MediaRecorder | null = null;
    private recordedChunks: BlobPart[] = [];
    private pausedChunks: PausedChunk[] = [];
    private audioContext: AudioContext | null = null;
    private analyserNode: AnalyserNode | null = null;
    private analyserFrameId: number | null = null;
    private isSpeechActive: boolean = false;
    private lastSpeechDetectedAt: number = 0;
    private hasDetectedSpeech: boolean = false;
    private noSpeechTimeoutId: number | null = null;
    private maxRecordingTimeoutId: number | null = null;
    private statusHideTimeoutId: number | null = null;
    private stoppedByNoSpeechTimeout: boolean = false;
    private stoppedByMaxDuration: boolean = false;
    private sendRecord: boolean = true;
    private currentStream: MediaStream | null = null;
    private currentRecordingMimeType: string | null = null;
    private currentInputHasText: boolean = false;
    private chatMessageInputHasTextListener: EventListener | null = null;
    private micDeniedOnce: boolean = false;

    showStartConfirm: boolean = false;
    showMicPermissionDialog: boolean = false;
    showRemoveSpeechButton: boolean = false;
    isMicrophonePermissionDenied: boolean = false;

    readonly SPEECH_THRESHOLD: number = 0.01;
    readonly PAUSE_HOLD_MS: number = 900;
    readonly PRE_SPEECH_BUFFER_MS: number = 350;
    readonly NO_SPEECH_TIMEOUT_MS: number = 60000;
    readonly MAX_RECORDING_TIME_MS: number = 60000;
    readonly STATUS_HIDE_DELAY_MS: number = 4000;
    readonly IOS_PERMISSION_API_MIN_VERSION: string = "2.2.12";
    readonly ANDROID_PERMISSION_API_MIN_VERSION: string = "3.13.0";
    readonly PREFERRED_AUDIO_MIME_TYPES: string[] = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
    ];

    constructor(
        private readonly widgetConfig: WidgetConfig,
        private readonly componentConfig: ComponentConfig,
    ) {
        this.speechMessageComponent = this as unknown as AlpineMagics<SpeechMessage>;
    }

    private getSpeechToTextUrl(): string {
        return (this.widgetConfig as WidgetConfig & SpeechToTextUrlProvider).getSpeechToTextUrl();
    }

    // noinspection JSUnusedGlobalSymbols
    init(): void {
        // Register listeners in Alpine lifecycle so state mutations run through reactive proxy.
        this.initEventListeners();
        this.requestCurrentInputState();
        void this.syncMicrophonePermissionState();
    }

    private setMicrophonePermissionDenied(isDenied: boolean): void {
        this.isMicrophonePermissionDenied = isDenied;
    }

    private updateMicrophonePermissionState(permissionState: string): void {
        this.setMicrophonePermissionDenied(permissionState === 'denied');
    }

    private async syncMicrophonePermissionState(): Promise<void> {
        const navigatorWithPermissions = navigator as NavigatorWithPermissions;
        if (
            navigatorWithPermissions.permissions === undefined
            || typeof navigatorWithPermissions.permissions.query !== 'function'
        ) {
            return;
        }

        try {
            const permissionStatus = await navigatorWithPermissions.permissions.query({
                name: 'microphone' as PermissionName,
            });

            this.updateMicrophonePermissionState(permissionStatus.state);
            permissionStatus.onchange = (): void => {
                this.updateMicrophonePermissionState(permissionStatus.state);
            };
        } catch (error: unknown) {
            void logContext(
                'syncMicrophonePermissionState',
                `Permission query unavailable: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    private handleChatMessageInputHasText(event: Event): void {
        const customEvent = event as CustomEvent<IChatMessageInputHasTextEventDetails>;
        this.currentInputHasText = customEvent.detail?.hasText ?? false;
        this.showRemoveSpeechButton = this.currentInputHasText;
    }

    private initEventListeners(): void {
        if (this.chatMessageInputHasTextListener !== null) {
            window.removeEventListener(
                CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT,
                this.chatMessageInputHasTextListener
            );
        }

        this.chatMessageInputHasTextListener = (event: Event): void => {
            this.handleChatMessageInputHasText(event);
        };

        window.addEventListener(
            CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT,
            this.chatMessageInputHasTextListener
        );
    }

    private requestCurrentInputState(): void {
        window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_INPUT_REQUEST_HAS_TEXT_EVENT));
    }

    private resolveSupportedAudioMimeType(): string | null {
        if (typeof MediaRecorder.isTypeSupported !== "function") {
            return this.PREFERRED_AUDIO_MIME_TYPES[0];
        }

        for (const mimeType of this.PREFERRED_AUDIO_MIME_TYPES) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
                return mimeType;
            }
        }

        return null;
    }

    async handleStartButtonClick(): Promise<void> {
        this.requestCurrentInputState();

        if (this.currentInputHasText) {
            this.showStartConfirm = true;
        } else {
            await this.handleStartClick();
        }
    }

    async handleConfirmStart(): Promise<void> {
        this.showStartConfirm = false;
        this.handleRemoveSpeech();
        await this.handleStartClick();
    }

    handleCancelConfirm(): void {
        this.showStartConfirm = false;
    }

    async handleRetryMicPermission(): Promise<void> {
        this.showMicPermissionDialog = false;
        this.micDeniedOnce = false;
        // await this.handleStartClick(true);
    }

    private isMicrophonePermissionError(error: unknown): boolean {
        if (error instanceof DOMException) {
            return error.name === 'NotAllowedError' ||
                error.name === 'PermissionDeniedError' ||
                error.name === 'SecurityError';
        }

        if (error instanceof Error) {
            return /notallowed|permission|denied|security/i.test(error.name + ' ' + error.message);
        }

        return false;
    }

    private resolveAppUserAgent(): string {
        const configData = this.componentConfig as ComponentConfig & {
            xAppUserAgent?: string;
            ['x-app-user-agent']?: string;
        };

        if (typeof configData.appUserAgent === "string" && configData.appUserAgent.trim() !== "") {
            return configData.appUserAgent;
        }
        if (typeof configData.xAppUserAgent === "string" && configData.xAppUserAgent.trim() !== "") {
            return configData.xAppUserAgent;
        }
        if (typeof configData['x-app-user-agent'] === "string" && configData['x-app-user-agent'].trim() !== "") {
            return configData['x-app-user-agent'];
        }

        return "";
    }

    private extractPlatformVersion(appUserAgent: string, platform: "ios" | "android"): string | null {
        const versionMatch = appUserAgent.match(/\b[^\s\/]+[\/\s](\d+\.\d+\.\d+)/i);
        if (versionMatch === null || versionMatch[1] === undefined) {
            return null;
        }

        const normalizedUserAgent = appUserAgent.toLowerCase();
        if (platform === "ios" && !/ios|iphone|ipad/.test(normalizedUserAgent)) {
            return null;
        }

        if (platform === "android" && !/android/.test(normalizedUserAgent)) {
            return null;
        }

        return versionMatch[1];
    }

    private compareSemver(leftVersion: string, rightVersion: string): number {
        const leftParts = leftVersion.split(".").map((part) => Number.parseInt(part, 10));
        const rightParts = rightVersion.split(".").map((part) => Number.parseInt(part, 10));
        const maxLength = Math.max(leftParts.length, rightParts.length);

        for (let index = 0; index < maxLength; index++) {
            const left = Number.isFinite(leftParts[index]) ? leftParts[index] : 0;
            const right = Number.isFinite(rightParts[index]) ? rightParts[index] : 0;

            if (left > right) {
                return 1;
            }
            if (left < right) {
                return -1;
            }
        }

        return 0;
    }

    private async supportsNativeMicPermissionApis(platform: "ios" | "android"): Promise<boolean> {
        const appUserAgent = this.resolveAppUserAgent();
        await logContext('supportsNativeMicPermissionApis', `platform=${platform}, appUserAgent=${appUserAgent}`);
        
        if (appUserAgent === "") {
            await logContext('supportsNativeMicPermissionApis', 'appUserAgent is empty, returning false');
            return false;
        }

        const version = this.extractPlatformVersion(appUserAgent, platform);
        await logContext('supportsNativeMicPermissionApis', `Extracted version: ${version}`);
        
        if (version === null) {
            await logContext('supportsNativeMicPermissionApis', 'version is null, returning false');
            return false;
        }

        const minimumVersion = platform === "ios"
            ? this.IOS_PERMISSION_API_MIN_VERSION
            : this.ANDROID_PERMISSION_API_MIN_VERSION;
        
        await logContext('supportsNativeMicPermissionApis', `minimumVersion=${minimumVersion}, version=${version}`);
        
        const result = this.compareSemver(version, minimumVersion) >= 0;
        await logContext('supportsNativeMicPermissionApis', `Comparison result: ${result}`);
        
        return result;
    }

    private async startMicrophoneStream(retry: boolean): Promise<void> {
        this.showMicPermissionDialog = false;
        if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
            this.setStatus('Mikrofon wird in diesem Browser nicht unterstützt.');
            this.setMessageInputReadonly(false);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: false,
                    channelCount: 1,
                    sampleRate: 16000,
                },
            });

            this.setMicrophonePermissionDenied(false);
            this.currentStream = stream;
            const supportedAudioMimeType = this.resolveSupportedAudioMimeType();
            if (supportedAudioMimeType === null) {
                this.setStatus("Audio-Aufnahme wird in diesem Browser nicht unterstützt.");
                this.stopCurrentStream();
                this.setControls(false, true);
                return;
            }

            this.currentRecordingMimeType = supportedAudioMimeType;
            this.mediaRecorder = new MediaRecorder(stream, { mimeType: supportedAudioMimeType });
            this.recordedChunks = [];
            this.pausedChunks = [];
            this.isSpeechActive = false;
            this.hasDetectedSpeech = false;
            this.stoppedByNoSpeechTimeout = false;
            this.stoppedByMaxDuration = false;
            this.lastSpeechDetectedAt = Date.now();

            this.hidePlayback();
            this.startPauseDetection(stream);
            this.startNoSpeechTimeout();
            this.startMaxRecordingTimeout();

            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data.size <= 0) {
                    return;
                }

                const now = Date.now();
                if (this.isSpeechActive || (now - this.lastSpeechDetectedAt) <= this.PAUSE_HOLD_MS) {
                    this.recordedChunks.push(event.data);
                    return;
                }

                this.pausedChunks.push({ data: event.data, recordedAt: now });
                this.pausedChunks = this.pausedChunks.filter(
                    (chunkEntry) => (now - chunkEntry.recordedAt) <= this.PRE_SPEECH_BUFFER_MS
                );
            };

            this.mediaRecorder.onstop = () => {
                this.hideRecordingVisualizer();
                this.stopPauseDetection();
                this.clearNoSpeechTimeout();
                this.clearMaxRecordingTimeout();

                if (this.recordedChunks.length === 0) {
                    this.setStatus(
                        this.stoppedByNoSpeechTimeout
                            ? "Keine Sprache erkannt. Aufnahme wurde nach Inaktivität automatisch beendet."
                            : (this.stoppedByMaxDuration
                                ? "Maximale Aufnahmedauer erreicht. Bitte erneut aufnehmen."
                                : "Keine Sprache erkannt. Bitte erneut aufnehmen.")
                    );
                    this.stopCurrentStream();
                    return;
                }

                const blob = new Blob(this.recordedChunks, {
                    type: this.currentRecordingMimeType ?? this.PREFERRED_AUDIO_MIME_TYPES[0]
                });

                if (this.audioPlayback instanceof HTMLAudioElement) {
                    this.audioPlayback.src = URL.createObjectURL(blob);
                    this.audioPlayback.style.display = "block";
                }
                if (this.sendRecord) {
                    this.sendRecording(blob);
                }
                this.sendRecord = true;

                this.stopCurrentStream();
            };

            this.mediaRecorder.start(250);
            this.setControls(true, false);
            this.showRecordingVisualizer();

        } catch (error: unknown) {
            this.clearNoSpeechTimeout();
            this.clearMaxRecordingTimeout();
            this.stopPauseDetection();
            this.stopCurrentStream();
            this.setControls(false, true);

            const errorMessage = error instanceof Error ? error.message : String(error);

            if (this.isMicrophonePermissionError(error) && !retry) {
                this.micDeniedOnce = true;
                this.setMicrophonePermissionDenied(true);
                this.showMicPermissionDialog = true;
                this.setMessageInputReadonly(false);
                return;
            }

            this.setMessageInputReadonly(false);
            console.error("MediaRecorder error:", error);
        }
    }

    private async handleNativeMicrophonePermissionCompleted(status: string): Promise<void> {
        await logContext('handleNativeMicrophonePermissionCompleted', `Entry: status=${status}`);
        const normalizedStatus = String(status).trim().toLowerCase();
        await logContext('handleNativeMicrophonePermissionCompleted', `Normalized status: ${normalizedStatus}`);
        this.updateMicrophonePermissionState(normalizedStatus);

        if (normalizedStatus === "authorized") {
            await logContext('handleNativeMicrophonePermissionCompleted', 'Status is authorized, starting microphone stream');
            await this.startMicrophoneStream(false);
            return;
        }

        await logContext('handleNativeMicrophonePermissionCompleted', 'Status is NOT authorized, showing permission dialog');
        this.stopCurrentStream();
        this.setControls(false, true);
        this.showMicPermissionDialog = true;
    }

    async handleStartClick(retry: boolean = false): Promise<void> {
        await logContext('handleStartClick', `Entry: retry=${retry}, mediaRecorderState=${this.mediaRecorder?.state}`);

        if (this.mediaRecorder?.state === "recording") {
            await logContext('handleStartClick', 'Already recording, returning early');
            return;
        }

        this.setMessageInputReadonly(true);

        const startButton = this.speechMessageComponent.$refs.startButton;
        const cancelButton = this.speechMessageComponent.$refs.cancelButton;
        const stopButton = this.speechMessageComponent.$refs.stopButton;
        const recordStatus = this.speechMessageComponent.$refs.recordStatus;
        const audioRecord = this.speechMessageComponent.$refs.audioRecord;
        const sendButton = this.speechMessageComponent.$refs.sendButton;

        this.startBtn = startButton instanceof HTMLButtonElement ? startButton : null;
        this.cancelBtn = cancelButton instanceof HTMLButtonElement ? cancelButton : null;
        this.stopBtn = stopButton instanceof HTMLButtonElement ? stopButton : null;
        this.sendBtn = sendButton instanceof HTMLButtonElement ? sendButton : null;
        this.statusDiv = recordStatus instanceof HTMLElement ? recordStatus : null;
        this.audioPlayback = audioRecord instanceof HTMLAudioElement ? audioRecord : null;

        const browserWindow = window as BrowserWindow;
        await logContext('handleStartClick', `browserWindow.androidBridge: ${browserWindow.androidBridge !== undefined}`);
        
        try {
            if (browserWindow.androidBridge !== undefined) {
                await logContext('handleStartClick', 'Entering Android permission flow');
                await logContext('handleStartClick', 'Calling androidBridge.checkMicrophonePermissions()');
                
                const permissionStatus = await browserWindow.androidBridge.checkMicrophonePermissions();
                await logContext('checkMicrophonePermissions', `Raw response: ${String(permissionStatus)}`);
                
                const normalizedPermissionStatus = String(permissionStatus).trim().toLowerCase();
                await logContext('checkMicrophonePermissions', `Normalized: ${normalizedPermissionStatus}`);
                this.updateMicrophonePermissionState(normalizedPermissionStatus);
                
                if (normalizedPermissionStatus !== "authorized") {
                    await logContext('handleStartClick', 'Permission not authorized, setting up callback and requesting');
                    
                    browserWindow.microphonePermissionRequestCompleted = async (status: string): Promise<void> => {
                        const normalizedStatus = String(status).trim().toLowerCase();
                        await logContext('microphonePermissionRequestCompleted', `Callback invoked with status: ${normalizedStatus}`);
                        await this.handleNativeMicrophonePermissionCompleted(status);
                    };
                    
                    await logContext('handleStartClick', 'Calling androidBridge.requestMicrophonePermissions()');
                    browserWindow.androidBridge.requestMicrophonePermissions();
                    await logContext('handleStartClick', 'requestMicrophonePermissions() call completed, returning');
                    return;
                }

                await logContext('handleStartClick', 'Permission already authorized, starting microphone stream');
                await this.startMicrophoneStream(retry);
                return;
            }

            await logContext('handleStartClick', 'Checking iOS permission flow');
            const iosHandlers = browserWindow.webkit?.messageHandlers;
            if (
                iosHandlers !== undefined &&
                iosHandlers.checkMicrophonePermissions !== undefined &&
                iosHandlers.requestMicrophonePermissions !== undefined
            ) {
                await logContext('handleStartClick', 'Entering iOS permission flow');
                const permissionStatus = await iosHandlers.checkMicrophonePermissions.postMessage({});
                const normalizedPermissionStatus = String(permissionStatus).trim().toLowerCase();
                await logContext('checkMicrophonePermissions', `iOS normalized: ${normalizedPermissionStatus}`);
                this.updateMicrophonePermissionState(normalizedPermissionStatus);
                
                if (normalizedPermissionStatus !== "authorized") {
                    browserWindow.microphonePermissionRequestCompleted = async (status: string): Promise<void> => {
                        await logContext('microphonePermissionRequestCompleted', `iOS callback: ${String(status).trim().toLowerCase()}`);
                        await this.handleNativeMicrophonePermissionCompleted(status);
                    };
                    iosHandlers.requestMicrophonePermissions.postMessage({});
                    return;
                }
                await this.startMicrophoneStream(retry);
                return;
            }
            
            await logContext('handleStartClick', 'No native permission APIs available, proceeding to standard flow');
        } catch (error: unknown) {
            await logContext('handleStartClick', `Exception caught: ${error instanceof Error ? error.message : String(error)}`);
            this.showMicPermissionDialog = true;
            this.setMessageInputReadonly(false);
            return;
        }

        if (this.micDeniedOnce && !retry) {
            await logContext('handleStartClick', 'micDeniedOnce=true and retry=false, showing permission dialog');
            this.showMicPermissionDialog = true;
            this.setMessageInputReadonly(false);
            return;
        }

        await logContext('handleStartClick', 'Calling startMicrophoneStream');
        await this.startMicrophoneStream(retry);
    }

    handleCancelClick(): void {
        if (this.mediaRecorder === null || this.mediaRecorder.state === "inactive") {
            return;
        }

        this.clearNoSpeechTimeout();
        this.clearMaxRecordingTimeout();
        this.sendRecord = false;
        this.mediaRecorder.stop();
        this.handleRemoveSpeech();
        this.setControls(false, true);
        this.setStatus('Aufnahme wurde abgebrochen ...');
    }

    handleStopClick(): void {
        if (this.mediaRecorder === null || this.mediaRecorder.state === "inactive") {
            return;
        }

        this.hideRecordingVisualizer();
        this.clearNoSpeechTimeout();
        this.clearMaxRecordingTimeout();
        this.mediaRecorder.stop();
        this.setControls(false, true);
    }

    handleRemoveSpeech(): void {
        this.currentInputHasText = false;
        this.showRemoveSpeechButton = false;

        const detail: IChatMessageInputFillRecordedTextEventDetails = { text: '' };
        window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT, {
            detail,
        }));
    }


    private setStatus(message: string): void {
        if (this.statusDiv !== null) {
            this.clearStatusHideTimeout();
            this.hideRecordingVisualizer();
            this.statusDiv.textContent = message;

            const statusMessage = message;
            this.statusHideTimeoutId = window.setTimeout(() => {
                if (this.statusDiv !== null && this.statusDiv.textContent === statusMessage) {
                    this.statusDiv.textContent = '';
                }
                this.statusHideTimeoutId = null;
            }, this.STATUS_HIDE_DELAY_MS);
        }
    }

    private setControls(startDisabled: boolean, stopDisabled: boolean): void {
        if(this.startBtn instanceof HTMLButtonElement) {
            if (startDisabled) {
                this.startBtn.setAttribute('disabled', 'disabled');
                this.startBtn.hidden = true;
                this.startBtn.classList.add('hide');
            } else {
                this.startBtn.removeAttribute('disabled');
                this.startBtn.hidden = false;
                this.startBtn.classList.remove('hide');
            }
        }

        if(this.cancelBtn instanceof HTMLButtonElement) {
            // Show cancel button exactly when start button is disabled.
            this.cancelBtn.hidden = !startDisabled;
            this.cancelBtn.classList.toggle('hide', this.cancelBtn.hidden);

            if (stopDisabled || !startDisabled) {
                this.cancelBtn.setAttribute('disabled', 'disabled');
            } else {
                this.cancelBtn.removeAttribute('disabled');
            }
        }

        if(this.stopBtn instanceof HTMLButtonElement) {
            if (stopDisabled) {
                this.stopBtn.setAttribute('disabled', 'disabled');
                this.stopBtn.hidden = true;
                this.stopBtn.classList.add('hide');
            } else {
                this.stopBtn.removeAttribute('disabled');
                this.stopBtn.hidden = false;
                this.stopBtn.classList.remove('hide');
            }
        }
        if (this.sendBtn instanceof HTMLButtonElement) {
            if (! stopDisabled) {
                this.sendBtn.setAttribute('disabled', 'disabled');
                this.sendBtn.hidden = true;
                this.sendBtn.classList.add('hide');
            } else {
                this.sendBtn.removeAttribute('disabled');
                this.sendBtn.hidden = false;
                this.sendBtn.classList.remove('hide');
            }
        }

        this.setMessageInputReadonly(!stopDisabled);
    }

    private resolveMessageInput(): HTMLTextAreaElement | null {
        const scopeNode = this.startBtn?.getRootNode()
            ?? this.stopBtn?.getRootNode()
            ?? this.cancelBtn?.getRootNode()
            ?? this.statusDiv?.getRootNode()
            ?? document;

        if (!(scopeNode instanceof Document || scopeNode instanceof ShadowRoot)) {
            return null;
        }

        const input = scopeNode.querySelector(SpeechMessage.CHAT_MESSAGE_INPUT_SELECTOR);
        return input instanceof HTMLTextAreaElement ? input : null;
    }

    private setMessageInputReadonly(isReadonly: boolean): void {
        const messageInput = this.resolveMessageInput();
        if (messageInput === null) {
            return;
        }

        messageInput.readOnly = isReadonly;
    }

    private showRecordingVisualizer(): void {
        if (this.statusDiv === null) {
            return;
        }

        this.clearStatusHideTimeout();
        this.statusDiv.textContent = '';

        const wrapper = document.createElement('div');
        wrapper.className = 'recording-visualizer';

        const canvas = document.createElement('canvas');
        canvas.className = 'recording-visualizer-canvas';
        canvas.width = 1;
        canvas.height = 24;

        const timer = document.createElement('span');
        timer.className = 'recording-visualizer-timer';
        const totalRecordingSeconds = Math.floor(this.MAX_RECORDING_TIME_MS / 1000);
        const initialMinutes = String(Math.floor(totalRecordingSeconds / 60)).padStart(2, '0');
        const initialSeconds = String(totalRecordingSeconds % 60).padStart(2, '0');
        timer.textContent = `${initialMinutes}:${initialSeconds}`;

        wrapper.appendChild(timer);
        wrapper.appendChild(canvas);
        this.statusDiv.appendChild(wrapper);

        this.recordingCanvas = canvas;
        this.recordingTimer = timer;
        this.recordingStartedAt = Date.now();
    }

    private clearStatusHideTimeout(): void {
        if (this.statusHideTimeoutId !== null) {
            window.clearTimeout(this.statusHideTimeoutId);
            this.statusHideTimeoutId = null;
        }
    }

    private hideRecordingVisualizer(): void {
        if (this.statusDiv !== null) {
            const visualizer = this.statusDiv.querySelector('.recording-visualizer');
            if (visualizer instanceof HTMLElement) {
                visualizer.remove();
            }
        }

        this.recordingCanvas = null;
        this.recordingTimer = null;
        this.recordingStartedAt = null;
    }

    private renderRecordingVisualizer(sampleBuffer: Uint8Array): void {
        if (this.recordingCanvas === null || this.recordingTimer === null || this.recordingStartedAt === null) {
            return;
        }

        const context = this.recordingCanvas.getContext('2d');
        if (context === null) {
            return;
        }

        const devicePixelRatio = window.devicePixelRatio || 1;
        const canvasBounds = this.recordingCanvas.getBoundingClientRect();
        const targetWidth = Math.max(1, Math.floor(canvasBounds.width * devicePixelRatio));
        const targetHeight = Math.max(1, Math.floor(canvasBounds.height * devicePixelRatio));
        if (this.recordingCanvas.width !== targetWidth || this.recordingCanvas.height !== targetHeight) {
            this.recordingCanvas.width = targetWidth;
            this.recordingCanvas.height = targetHeight;
        }

        const width = this.recordingCanvas.width;
        const height = this.recordingCanvas.height;

        context.clearRect(0, 0, width, height);
        context.fillStyle = '#000000';

        const barWidth = Math.max(2, Math.floor(width / 56));
        const barGap = Math.max(1, Math.floor(barWidth * 0.4));
        const barStep = barWidth + barGap;
        const barCount = Math.max(1, Math.floor(width / barStep));
        const sampleStep = Math.max(1, Math.floor(sampleBuffer.length / barCount));
        const centerY = height / 2;

        for (let index = 0; index < barCount; index++) {
            const rangeStart = index * sampleStep;
            const rangeEndExclusive = Math.min(sampleBuffer.length, rangeStart + sampleStep);
            let peakNormalized = 0;
            for (let sampleIndex = rangeStart; sampleIndex < rangeEndExclusive; sampleIndex++) {
                const sample = sampleBuffer[sampleIndex] ?? 128;
                const normalized = Math.abs((sample - 128) / 128);
                if (normalized > peakNormalized) {
                    peakNormalized = normalized;
                }
            }

            const boosted = Math.min(1, peakNormalized * 1.8);
            const visualizedAmplitude = Math.pow(boosted, 0.7);
            const barHeight = Math.max(2, visualizedAmplitude * (height - 2));
            const x = index * barStep;
            const y = centerY - (barHeight / 2);
            context.fillRect(x, y, barWidth, barHeight);
        }

        const elapsedMs = Date.now() - this.recordingStartedAt;
        const remainingSeconds = Math.max(0, Math.ceil((this.MAX_RECORDING_TIME_MS - elapsedMs) / 1000));
        const minutes = String(Math.floor(remainingSeconds / 60)).padStart(2, '0');
        const seconds = String(remainingSeconds % 60).padStart(2, '0');
        const shouldBlinkTimer = elapsedMs >= 55000;
        this.recordingTimer.classList.toggle('is-blinking', shouldBlinkTimer);
        this.recordingTimer.textContent = `${minutes}:${seconds}`;
    }


    private hidePlayback(): void {
        if (this.audioPlayback !== null) {
            this.audioPlayback.style.display = "none";
        }
    }

    private stopCurrentStream(): void {
        if (this.currentStream === null) {
            return;
        }

        this.currentStream.getTracks().forEach((track) => track.stop());
        this.currentStream = null;
    }

    private sendRecording(blob: Blob): void {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", this.getSpeechToTextUrl(), true);
        xhr.responseType = 'json';
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader("Content-Type", blob.type || this.currentRecordingMimeType || this.PREFERRED_AUDIO_MIME_TYPES[0]);

        xhr.onload = (): void => {
            const { message, errorCodeGroup } = this.parseSpeechToTextResponse(xhr);
            const isSuccess = xhr.status === 200 && errorCodeGroup.trim() === '';

            if (isSuccess) {
                const detail: IChatMessageInputFillRecordedTextEventDetails = { text: message };
                window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT, {
                    detail,
                }));
                return;
            }

            switch (errorCodeGroup) {
                case 'NoSpeechInput':
                    this.setStatus('Keine Sprache erkannt. Bitte erneut aufnehmen.');
                    break;
                case 'UnintelligibleSpeechInput':
                    this.setStatus('Sprache konnte nicht verstanden werden. Bitte deutlicher sprechen.');
                    break;
                case 'PoorAudioQuality':
                    this.setStatus('Audioqualität zu gering. Bitte in ruhiger Umgebung erneut aufnehmen.');
                    break;
                case 'SystemError':
                    this.setStatus('Ein Systemfehler ist aufgetreten. Bitte erneut versuchen.');
                    break;
                default:
                    this.setStatus(message !== '' ? message : `Senden fehlgeschlagen (HTTP ${xhr.status})`);
            }
        };

        xhr.send(blob);
    }

    private parseSpeechToTextResponse(xhr: XMLHttpRequest): SpeechToTextResponseData {
        let rawResponse: unknown = xhr.response;
        if (!rawResponse || typeof rawResponse !== 'object') {
            try {
                rawResponse = JSON.parse(xhr.responseText || '{}');
            } catch {
                rawResponse = {};
            }
        }

        const responseRecord = rawResponse as Record<string, unknown>;
        return {
            message: typeof responseRecord.message === 'string' ? responseRecord.message : '',
            errorCodeGroup: typeof responseRecord.errorCodeGroup === 'string' ? responseRecord.errorCodeGroup : '',
        };
    }

    private startPauseDetection(stream: MediaStream): void {
        const browserWindow = window as Window & { webkitAudioContext?: typeof AudioContext };
        const BrowserAudioContext = window.AudioContext || browserWindow.webkitAudioContext;
        if (!BrowserAudioContext) {
            return;
        }

        this.audioContext = new BrowserAudioContext();
        const sourceNode = this.audioContext.createMediaStreamSource(stream);
        this.analyserNode = this.audioContext.createAnalyser();
        this.analyserNode.fftSize = 2048;
        sourceNode.connect(this.analyserNode);

        const sampleBuffer = new Uint8Array(this.analyserNode.fftSize);

        const detectSpeech = (): void => {
            if (this.analyserNode === null) {
                return;
            }

            this.analyserNode.getByteTimeDomainData(sampleBuffer);
            this.renderRecordingVisualizer(sampleBuffer);
            let sumOfSquares = 0;
            for (let i = 0; i < sampleBuffer.length; i++) {
                const normalized = (sampleBuffer[i] - 128) / 128;
                sumOfSquares += normalized * normalized;
            }

            const rms = Math.sqrt(sumOfSquares / sampleBuffer.length);
            const now = Date.now();
            if (rms >= this.SPEECH_THRESHOLD) {
                this.isSpeechActive = true;
                this.lastSpeechDetectedAt = now;
                this.hasDetectedSpeech = true;
                this.clearNoSpeechTimeout();

                if (this.pausedChunks.length > 0) {
                    this.pausedChunks.forEach((chunkEntry) => this.recordedChunks.push(chunkEntry.data));
                    this.pausedChunks = [];
                }
            } else if (this.isSpeechActive && (now - this.lastSpeechDetectedAt) > this.PAUSE_HOLD_MS) {
                this.isSpeechActive = false;
            }

            this.analyserFrameId = window.requestAnimationFrame(detectSpeech);
        };

        detectSpeech();
    }

    private stopPauseDetection(): void {
        if (this.analyserFrameId !== null) {
            window.cancelAnimationFrame(this.analyserFrameId);
            this.analyserFrameId = null;
        }

        this.analyserNode = null;
        if (this.audioContext !== null) {
            void this.audioContext.close();
            this.audioContext = null;
        }
    }

    private clearNoSpeechTimeout(): void {
        if (this.noSpeechTimeoutId !== null) {
            window.clearTimeout(this.noSpeechTimeoutId);
            this.noSpeechTimeoutId = null;
        }
    }

    private clearMaxRecordingTimeout(): void {
        if (this.maxRecordingTimeoutId !== null) {
            window.clearTimeout(this.maxRecordingTimeoutId);
            this.maxRecordingTimeoutId = null;
        }
    }

    private startNoSpeechTimeout(): void {
        this.clearNoSpeechTimeout();
        if (this.NO_SPEECH_TIMEOUT_MS >= this.MAX_RECORDING_TIME_MS) {
            return;
        }

        this.noSpeechTimeoutId = window.setTimeout(() => {
            if (this.mediaRecorder === null || this.mediaRecorder.state === "inactive" || this.hasDetectedSpeech) {
                return;
            }

            this.stoppedByNoSpeechTimeout = true;
            this.mediaRecorder.stop();
            this.setControls(false, true);
        }, this.NO_SPEECH_TIMEOUT_MS);
    }

    private startMaxRecordingTimeout(): void {
        this.clearMaxRecordingTimeout();
        this.maxRecordingTimeoutId = window.setTimeout(() => {
            if (this.mediaRecorder === null || this.mediaRecorder.state === "inactive") {
                return;
            }

            this.stoppedByMaxDuration = true;
            this.mediaRecorder.stop();
            this.setControls(false, true);
        }, this.MAX_RECORDING_TIME_MS);
    }
}

const speechMessageFactory: SpeechMessageFactory = (
    widget: ChatWidget,
    widgetConfig: WidgetConfig,
    componentConfig: ComponentConfig
) => {
    // Keep the factory signature consistent with other components.
    void widget;

    return new SpeechMessage(widgetConfig, componentConfig) as AlpineComponent<SpeechMessage>;
};

export default speechMessageFactory;
